import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { fireStore } from "../../firebase";
import { EventTypeFilterEnum } from "../../constants/enums";
import { IChatsRecord } from "../../interfaces/chats";

const EVENTS_COLLECTION = "events";
const CORPORATE_EVENTS_COLLECTION = "corporateEvents";
/**
 * Subscribes to Firestore for each event in the list. When any event document is updated,
 * calls onEventChanged so the UI can refetch or update that event (e.g. getChatRecordByEventId).
 *
 * @param shouldStart - When true and events has length, subscribes; otherwise does nothing.
 * @param events - List of chat records (each with eventId and eventType) to listen to.
 * @param onEventChanged - Callback when an event doc changes. Called with that event's eventId.
 *
 * @example
 * useOnUpdateEventsInList(shouldStartUpdating, events, (eventId) => {
 *   dispatch(getChatRecordByEventId({ eventId, signal: controller.signal }));
 * });
 */
export function useOnUpdateEventsInList(
	shouldStart: boolean,
	events: IChatsRecord[],
	onEventChanged: (eventId: string) => void,
) {
	const initializedRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!fireStore || !shouldStart || !events?.length) return;
		const db = fireStore;

		const unsubscribes: (() => void)[] = [];

		events.forEach((ev) => {
			const eventId = ev.eventId;
			if (!eventId) return;

			const collection =
				ev.eventType === EventTypeFilterEnum.B2B ? CORPORATE_EVENTS_COLLECTION : EVENTS_COLLECTION;

			const documentRef = doc(db, collection, eventId);

			const unsubscribe = onSnapshot(documentRef, (snapshot) => {
				// console.log("eventId -> ", eventId);
				// Ignore initial snapshot PER DOCUMENT
				if (!initializedRef.current.has(eventId)) {
					initializedRef.current.add(eventId);
					return;
				}

				if (snapshot.exists()) {
					onEventChanged(eventId);
				}
			});

			unsubscribes.push(unsubscribe);
		});

		return () => {
			unsubscribes.forEach((unsub) => unsub());
			initializedRef.current.clear();
		};
	}, [shouldStart, events, onEventChanged]);
}
